/**
 * auth.store — store Zustand de autenticação.
 *
 * Suporta dois modos mutuamente exclusivos:
 *   • `jwt`   → usuário normal autenticado via /auth/login.
 *               accessToken vive em memória; refreshToken em localStorage
 *               (sobrevive a reload e alimenta /auth/refresh).
 *   • `basic` → admin global autenticado via HTTP Basic.
 *               basicCredentials = Base64(username:password), apenas em memória.
 *               Perdida no reload — admin precisa autenticar novamente.
 *
 * Estado inicial: tenta hidratar `refreshToken` do localStorage. Quem fica
 * responsável por chamar /auth/refresh ao boot é a camada de serviços/app
 * (não este store) — aqui apenas expomos o valor.
 */
import { create } from 'zustand'

import { STORAGE_KEYS } from '@/lib/constants'
import type { TokenResponse, User } from '@/types/api'

export type AuthMode = 'jwt' | 'basic' | null

export interface AuthState {
  mode: AuthMode
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  basicCredentials: string | null
}

export interface AuthActions {
  /** Define autenticação JWT a partir da resposta de /auth/login (ou /refresh). */
  setJwtAuth: (tokens: TokenResponse) => void
  /** Define autenticação HTTP Basic — codifica username:password em Base64. */
  setBasicAuth: (username: string, password: string) => void
  /** Atualiza dados do usuário corrente (GET /me). Somente faz sentido no modo jwt. */
  setUser: (user: User) => void
  /** Limpa todo o estado de autenticação e remove o refreshToken do localStorage. */
  clearAuth: () => void
}

export type AuthStore = AuthState & AuthActions

/**
 * Codifica `username:password` em Base64 para uso em `Authorization: Basic …`.
 *
 * `btoa` está disponível tanto em browsers quanto no jsdom usado nos testes.
 * Fallback para `Buffer` cobre execução em Node puro (CI sem jsdom).
 */
function encodeBasicCredentials(username: string, password: string): string {
  const raw = `${username}:${password}`
  if (typeof btoa === 'function') return btoa(raw)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buf = (globalThis as any).Buffer
  if (buf && typeof buf.from === 'function') {
    return buf.from(raw, 'utf-8').toString('base64')
  }
  throw new Error('No Base64 encoder available in this environment')
}

/** Lê o refreshToken persistido (silenciosamente retorna `null` se localStorage falhar). */
function readPersistedRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEYS.AUTH)
  } catch {
    return null
  }
}

function writePersistedRefreshToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(STORAGE_KEYS.AUTH)
    else window.localStorage.setItem(STORAGE_KEYS.AUTH, token)
  } catch {
    // localStorage indisponível (modo privado / SSR) — ignora silenciosamente.
  }
}

const initialRefreshToken = typeof window !== 'undefined' ? readPersistedRefreshToken() : null

export const useAuthStore = create<AuthStore>((set) => ({
  mode: null,
  user: null,
  accessToken: null,
  refreshToken: initialRefreshToken,
  basicCredentials: null,

  setJwtAuth: (tokens) => {
    writePersistedRefreshToken(tokens.refresh_token)
    set({
      mode: 'jwt',
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      basicCredentials: null,
    })
  },

  setBasicAuth: (username, password) => {
    const credentials = encodeBasicCredentials(username, password)
    // Limpa qualquer resíduo JWT — modos são mutuamente exclusivos.
    writePersistedRefreshToken(null)
    set({
      mode: 'basic',
      basicCredentials: credentials,
      accessToken: null,
      refreshToken: null,
      user: null,
    })
  },

  setUser: (user) => set({ user }),

  clearAuth: () => {
    writePersistedRefreshToken(null)
    set({
      mode: null,
      user: null,
      accessToken: null,
      refreshToken: null,
      basicCredentials: null,
    })
  },
}))
